import { jwtDecode } from 'jwt-decode'
import { clearUserLocalStorage } from './userLocalStorage'
import { beginSessionEnd } from './sessionLifecycle'

const ADMIN_ACCESS_TOKEN_KEY = 'adminAccessToken'
const ADMIN_REFRESH_TOKEN_KEY = 'adminRefreshToken'

interface AccessTokenPayload {
  impersonation?: boolean
}

export function isImpersonationAccessToken(token?: string | null): boolean {
  if (!token) {
    return false
  }

  try {
    const decoded = jwtDecode<AccessTokenPayload>(token)
    return decoded.impersonation === true
  } catch {
    return false
  }
}

export function saveAdminTokensForImpersonation() {
  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')

  if (accessToken) {
    sessionStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, accessToken)
  }
  if (refreshToken) {
    sessionStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken)
  }
}

export function hasStoredAdminTokens(): boolean {
  return !!sessionStorage.getItem(ADMIN_ACCESS_TOKEN_KEY)
}

export function restoreAdminTokensFromImpersonation(): boolean {
  const accessToken = sessionStorage.getItem(ADMIN_ACCESS_TOKEN_KEY)
  const refreshToken = sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY)

  if (!accessToken) {
    return false
  }

  localStorage.setItem('accessToken', accessToken)
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken)
  } else {
    localStorage.removeItem('refreshToken')
  }

  sessionStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY)
  sessionStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY)
  return true
}

export function clearStoredAdminTokens() {
  sessionStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY)
  sessionStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY)
}

export function endImpersonationSession(redirectTo = '/admin') {
  beginSessionEnd()
  const restored = restoreAdminTokensFromImpersonation()
  if (!restored) {
    clearUserLocalStorage({ keepChapterBackups: true })
  }

  if (window.location.pathname !== redirectTo) {
    window.location.href = redirectTo
    return
  }

  window.location.reload()
}

export function markExpectedSessionEnd(error: unknown) {
  if (error && typeof error === 'object') {
    Object.defineProperty(error, 'isExpectedSessionEnd', {
      value: true,
      enumerable: false,
    })
  }
}

export function isExpectedSessionEndError(error: unknown): boolean {
  return !!(error && typeof error === 'object' && (error as { isExpectedSessionEnd?: boolean }).isExpectedSessionEnd)
}

export function markRefreshFailureHandled(error: unknown) {
  if (error && typeof error === 'object') {
    Object.defineProperty(error, 'isRefreshFailureHandled', {
      value: true,
      enumerable: false,
    })
  }
}

export function isRefreshFailureHandled(error: unknown): boolean {
  return !!(error && typeof error === 'object' && (error as { isRefreshFailureHandled?: boolean }).isRefreshFailureHandled)
}
